"use client"

import styled from "@emotion/styled"
import { Box, Button, useMediaQuery, useTheme } from "@mui/material"
import { Image } from "next/dist/client/image-component"
import { useState } from "react"
import { Desktop, Mobile, NewText, RishoPhone, RishoWhatsApp } from "ui"
import { extractProperty, extractPropertyMediaItems, getUmbracoMediaUrl } from "utils"
import LandingLeadForm from "./LandingLeadForm"

interface IContactButton {
  name?: string
  target?: null | "_blank" | "_self" | "_parent" | "_top"
  url?: string
  type?: "EXTERNAL" | any
  __typename?: any
}

const StyledFormWrapper = styled.div`
  background-color: #ededeb;
  height: 100%;
  color: ${(props) => props.theme.colors.black};
  ${(props) => props.theme.breakpoints.up("md")} {
    max-height: 100vh;
    overflow-y: auto;
  }
`

const StyledImageSwapper = styled.div`
  position: relative;
  width: 150px;
  height: 181px;
  margin: 8px auto;
  margin-top: 40px;
`

const StyledTitlesWrapper = styled(Box)`
  padding: 0px 29px 0px 21px;
  margin-bottom: 16px;
`

const StyledBottomLogosWrapper = styled(Box)`
  padding: 40px 28px 32px 21.11px;
  display: flex;
  flex-direction: row-reverse;
  justify-content: space-between;
  flex-wrap: wrap;
  ${(props) => props.theme.breakpoints.up("md")} {
    padding: 40px 24px 24px;
    display: none;
  }
  ${(props) => props.theme.breakpoints.up("lg")} {
    display: flex;
  }
  ${(props) => props.theme.breakpoints.up("xl")} {
    padding: 40px 43px 24px 40px;
  }
`

const StyledBottomLogos = styled(Image)`
  height: 50px;
  min-width: 95px;
  width: fit-content;
  object-fit: contain;
  object-position: left;
  ${(props) => props.theme.breakpoints.up("md")} {
    height: 68px;
    min-width: 158px;
    object-position: center;
  }
`

const ButtonsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  width: 100%;
  gap: 16px;

  padding-top: 24px;
  padding-inline: 16px;
  ${(props) => props.theme.breakpoints.up("md")} {
    padding-top: 32px;
    padding-inline: 40px;
  }
`

const StyledButton = styled(Button)`
  gap: 8px;
  border-radius: 12.65px;
  font-size: 18px;
  font-weight: 600;
  letter-spacing: -0.18px;
  height: 41px;
  width: 100%;
  padding-block: 0;
  ${(props) => props.theme.breakpoints.up("md")} {
  }
  ${(props) => props.theme.breakpoints.down("sm")} {
    padding-inline: 2px;
  }
`

const CopiedNotification = styled.div<{ show: boolean }>`
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  background-color: ${(props) => props.theme.colors.black};
  color: white;
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  white-space: nowrap;
  margin-bottom: 8px;
  opacity: ${(props) => (props.show ? 1 : 0)};
  visibility: ${(props) => (props.show ? "visible" : "hidden")};
  transition: opacity 0.3s ease, visibility 0.3s ease;
  pointer-events: none;
  z-index: 10;

  &::after {
    content: "";
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 6px solid transparent;
    border-top-color: ${(props) => props.theme.colors.black};
  }
`

const CallButtonWrapper = styled.div`
  position: relative;
  width: 100%;
`

type Props = {
  locale: string
  data: any
  companyId?: number
  companyName?: string
  messages?: any
}

export const LandingFormSide: React.FC<Props> = ({ data, messages, locale }: Props) => {
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"))
  const [showCopied, setShowCopied] = useState(false)

  const pageLogo = extractProperty(data, "pageLogo")
  const pageTitle = extractProperty(data, "pageTitle")
  const shortDescription = extractProperty(data, "shortDescription")
  const bottomLogos = extractPropertyMediaItems(data, "bottomLogos")
  const pageForm = extractProperty(data, "pageForm")?.[0]?.properties
  const bmbyId = pageForm?.find((property: any) => property.propertyName === "bmbyId")
    ?.propertyValue?.value
  const formTitle = pageForm?.find((property: any) => property.propertyName === "fromTitle")
    ?.propertyValue?.value
  const formTextForMeiling = pageForm?.find(
    (property: any) => property.propertyName === "formTextForMeiling",
  )?.propertyValue?.value
  const formTextForSms = pageForm?.find(
    (property: any) => property.propertyName === "formTextForSms",
  )?.propertyValue?.value
  const formTextForTerms = pageForm
    ?.find((property: any) => property.propertyName === "formTextForTerms")
    ?.propertyValue?.richText?.replace(
      `/${locale === "he" ? "he-il" : "en-us"}/properties/pages`,
      locale === "he" ? "" : "/en",
    )
  const whatsAppButton: IContactButton = extractProperty(data, "whatsAppButton")?.[0] || null
  const callUsButton: IContactButton = extractProperty(data, "callUsButton")?.[0] || null

  const handleCallButtonClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
    if (isDesktop && callUsButton?.url) {
      e.preventDefault()
      const phoneNumber = callUsButton.url.replace(/^tel:/, "")
      try {
        await navigator.clipboard.writeText(phoneNumber)
        setShowCopied(true)
        setTimeout(() => setShowCopied(false), 3000)
      } catch (err) {
        console.error("Failed to copy phone number:", err)
      }
    }
  }

  return (
    <StyledFormWrapper id="landingLeadForm">
      <Desktop>
        <StyledImageSwapper>
          <Image
            src={getUmbracoMediaUrl(pageLogo)}
            alt="page logo"
            width={500}
            height={500}
            style={{
              height: "100%",
              width: "100%",
              objectFit: "cover",
            }}
            priority={true}
          />
        </StyledImageSwapper>
        <StyledTitlesWrapper>
          <NewText
            component={"h1"}
            desktopFontSize={35}
            desktopLineHeight={"61.65px"}
            desktopFontWeight={500}
            inheritColor
            textAlign={"center"}
            letterSpacing={"-0.01em"}
          >
            {pageTitle}
          </NewText>
          <NewText
            component={"h2"}
            desktopFontSize={20}
            desktopLineHeight={"24.2px"}
            desktopFontWeight={500}
            inheritColor
            marginTop={"1px"}
            textAlign={"center"}
            letterSpacing={"-0.015em"}
          >
            {shortDescription}
          </NewText>
        </StyledTitlesWrapper>
      </Desktop>
      <LandingLeadForm
        messages={messages}
        bmbyId={bmbyId}
        title={formTitle}
        formTextForMeiling={formTextForMeiling}
        formTextForTerms={formTextForTerms}
        formTextForSms={formTextForSms}
      />
      <ButtonsContainer>
        {whatsAppButton && whatsAppButton.name && whatsAppButton.url && (
          <StyledButton
            href={whatsAppButton.url}
            variant="contained"
            aria-label="Contact via WhatsApp"
          >
            <RishoWhatsApp />
            {whatsAppButton.name}
          </StyledButton>
        )}
        {callUsButton && callUsButton.name && callUsButton.url && (
          <CallButtonWrapper>
            <CopiedNotification show={showCopied}>
              {locale === "he" ? "המספר הועתק" : "Number copied"}
            </CopiedNotification>
            <StyledButton
              href={callUsButton.url}
              variant="contained"
              aria-label={`Call ${callUsButton.name}`}
              onClick={handleCallButtonClick}
            >
              <RishoPhone />
              {callUsButton.name}
            </StyledButton>
          </CallButtonWrapper>
        )}
      </ButtonsContainer>
      <StyledBottomLogosWrapper>
        {bottomLogos?.map((logo: string, key: number) => (
          <StyledBottomLogos
            key={key}
            src={getUmbracoMediaUrl(logo)}
            alt="footer logos"
            width={500}
            height={500}
            priority={true}
          />
        ))}
        <Mobile sx={{ width: "100%" }}>
          <NewText
            component={"p"}
            desktopFontSize={12.5}
            desktopLineHeight={"15px"}
            letterSpacing={"-0.01em"}
            inheritColor
            width={"100%"}
            marginTop={"23.6px"}
          >
            * הדמיות להמחשה בלבד. ט.ל.ח.
          </NewText>
        </Mobile>
      </StyledBottomLogosWrapper>
    </StyledFormWrapper>
  )
}

export default LandingFormSide
